import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../extra/Button";
import Table from "../../extra/Table";
import Title from "../../extra/Title";
import Pagination from "../../extra/Pagination";
import { thumb } from "../../../util/thumb";
import { useDispatch, useSelector } from "react-redux";
import { getAffiliate, blockUnblockAffiliate, deleteAffiliate } from "../../store/affiliate/affiliate.action";
import $ from "jquery";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import { colors } from "../../../util/SkeletonColor";
import { alert, warning } from "../../../util/Alert";

const Affiliate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all"); // all, active, blocked

  const { affiliate } = useSelector((state) => state.affiliate);

  useEffect(() => {
    dispatch(getAffiliate(page, size, search, status));
  }, [dispatch, page, size, search, status]);

  const handleBlockUnblock = (affiliateId, currentStatus) => {
    const data = warning();
    data
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(blockUnblockAffiliate(affiliateId));
          alert("Success", currentStatus ? "Creator blocked successfully!" : "Creator unblocked successfully!", "success");
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDelete = (affiliateId) => {
    const data = warning();
    data
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(deleteAffiliate(affiliateId));
          alert("Success", "Creator deleted successfully!", "success");
        }
      })
      .catch((err) => console.log(err));
  };

  const handleViewProfile = (row) => {
    navigate("/admin/affiliateProfile", { state: { affiliate: row } });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Pagination hands over the chosen number itself, not a DOM event.
  const handleChangeRowsPerPage = (value) => {
    setSize(parseInt(value, 10) || 25);
    setPage(1);
  };

  // Table configuration
  const mapData = [
    {
      Header: "No",
      width: "5%",
      Cell: ({ index }) => <span>{(page - 1) * size + index + 1}</span>,
    },
    {
      Header: "Creator",
      width: "20%",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <img
            src={thumb(row?.image || row?.logo) || "/dummy.png"}
            loading="lazy"
            decoding="async"
            alt="affiliate"
            height={45}
            width={45}
            className="rounded-circle me-2"
            style={{ width: 45, height: 45, minWidth: 45, objectFit: "cover" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/dummy.png";
            }}
          />
          <div>
            <div className="fw-bold">{row?.firstName} {row?.lastName}</div>
            <small className="text-muted">@{row?.businessTag}</small>
          </div>
        </div>
      ),
    },
    {
      Header: "Store Name",
      width: "15%",
      Cell: ({ row }) => <span>{row?.storeName || "N/A"}</span>,
    },
    {
      Header: "Category",
      width: "10%",
      Cell: ({ row }) => <span>{row?.category || "N/A"}</span>,
    },
    {
      Header: "Contact",
      width: "15%",
      Cell: ({ row }) => (
        <div>
          <div>{row?.mobileNumber}</div>
          <small className="text-muted">{row?.email}</small>
        </div>
      ),
    },
    {
      Header: "Commission",
      width: "8%",
      Cell: ({ row }) => (
        <span className="badge bg-info">{row?.commission || 0}%</span>
      ),
    },
    {
      Header: "Total Earnings",
      width: "10%",
      Cell: ({ row }) => <span>${row?.totalEarnings || "0.00"}</span>,
    },
    {
      Header: "Status",
      width: "8%",
      Cell: ({ row }) => (
        <span className={`badge ${row?.isBlock ? "bg-danger" : "bg-success"}`}>
          {row?.isBlock ? "Blocked" : "Active"}
        </span>
      ),
    },
    {
      Header: "Joined Date",
      width: "10%",
      Cell: ({ row }) => (
        <span>{dayjs(row?.createdAt).format("DD MMM YYYY")}</span>
      ),
    },
    {
      Header: "Action",
      width: "10%",
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-info"
            onClick={() => handleViewProfile(row)}
            title="View Profile"
          >
            <VisibilityIcon fontSize="small" />
          </button>
          <button
            className={`btn btn-sm ${row?.isBlock ? "btn-success" : "btn-warning"}`}
            onClick={() => handleBlockUnblock(row?._id, !row?.isBlock)}
            title={row?.isBlock ? "Unblock" : "Block"}
          >
            <BlockIcon fontSize="small" />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row?._id)}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="mainCategory">
      <Title
        name="Approved Creators"
        total={affiliate?.total || 0}
        totalShow
      />
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
                <div className="col-md-9">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, store, username..."
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                  </div>
                </div>
              </div>

              <Table
                data={affiliate?.data || []}
                mapData={mapData}
                type="server"
                className="table-responsive"
              />

              {/* These props were misnamed (activePage/rowsPerPage/userTotal/handle…),
                  so the control never rendered and only the first 10 of 16k creators
                  were reachable (2026-09-30). */}
              <Pagination
                component="div"
                type={"server"}
                count={affiliate?.total || 0}
                totalData={affiliate?.total || 0}
                serverPage={page}
                serverPerPage={size}
                setCurrentPage={setPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 500]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Affiliate;